import React, { useState, FocusEvent, ChangeEvent } from "react";
import { Card, Form, Button, Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, CreateUserData } from "@preveil-api";
import { AddUsersMessaging, CreateAccountUIActionTypes, RegexHelper, SupportRoutes } from "src/common";

// NOTE: Reusing admin messaging constants for shared display name validation rules.

type AllProps = {
  create_user_data?: CreateUserData;
  org_name?: string;
  handleAction: ActionHandlerFunction;
}

// Description: Checks if First Name and Last Name values are empty or only contain white spaces.
function isEmpty(value: string): boolean {
  return !value || !value.trim();
}

// Description: Validates a name field for forbidden characters and combined first + last name length.
// Returns an error message string, or empty string if valid.
function validateName(value: string, first: string, last: string): string {
  if (!RegexHelper.testDisplayNameChars(value)) {
    return AddUsersMessaging.invalid_display_name_chars;
  }
  if (`${first.trim()} ${last.trim()}`.length >= 256) {
    return AddUsersMessaging.invalid_display_name_length;
  }
  return "";
}

// Description: Handle Login submit send to parent component
function UserInformationFormComponent(props: AllProps) {
  const { create_user_data, org_name, handleAction } = props;
  const [first_name, setFirstName] = useState<string>("");
  const [last_name, setLastName] = useState<string>("");
  const [is_checked, setChecked] = useState<boolean>(false);
  const [first_name_error, setFirstNameError] = useState<string>("");
  const [last_name_error, setLastNameError] = useState<string>("");
  const is_form_valid = is_checked && !isEmpty(first_name) && !isEmpty(last_name) && !first_name_error && !last_name_error;

  // Description: Handle form submit
  function handleSubmit(e: FocusEvent<HTMLFormElement>) {
    e.stopPropagation();
    e.preventDefault();
    const current_first = e.currentTarget?.first_name?.value || first_name;
    const current_last = e.currentTarget?.last_name?.value || last_name;
    if (isEmpty(current_first) || isEmpty(current_last)) {
      return;
    }
    const fn_error = validateName(current_first, current_first, current_last);
    const ln_error = validateName(current_last, current_first, current_last);
    if (fn_error || ln_error) {
      setFirstNameError(fn_error);
      setLastNameError(ln_error);
      return;
    }
    handleAction({
      actionType: CreateAccountUIActionTypes.SubmitUserInformation,
      params: { first_name: current_first, last_name: current_last, display_name: `${current_first} ${current_last}` }
    });
  }

  return <>
    <Card.Body>
      <h3 className="border-bottom">
        {
          (!!org_name && !!create_user_data?.display_name) ?
            `${create_user_data.display_name}, you are almost done!` : "Enter your name"
        }
      </h3>
      {!!org_name && <p dangerouslySetInnerHTML={{
        __html: `Please review our license before <b>joining ${create_user_data?.org_name} organization</b> and start collaborating with your team.`
      }} />}
      <Form onSubmit={handleSubmit} >
        <Row className="mb-3">
          <Form.Group as={Col} className="col-8">
            <Form.Label htmlFor="first_name_tb" className="fs-6">First Name</Form.Label>
            <Form.Control
              id="first_name_tb"
              type="text"
              name="first_name"
              onChange={(e: ChangeEvent<HTMLInputElement>) => { setFirstName(e.currentTarget.value); setFirstNameError(""); }}
              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                setFirstName(value);
                setFirstNameError(validateName(value, value, last_name));
              }}
              value={first_name}
              autoFocus={true}
              isInvalid={!!first_name_error}
            />
            {!!first_name_error && <p className="error-message">{first_name_error}</p>}
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} className="col-8">
            <Form.Label htmlFor="last_name_tb" className="fs-6">Last Name</Form.Label>
            <Form.Control
              id="last_name_tb"
              type="text"
              name="last_name"
              onChange={(e: ChangeEvent<HTMLInputElement>) => { setLastName(e.currentTarget.value); setLastNameError(""); }}
              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                setLastName(value);
                setLastNameError(validateName(value, first_name, value));
              }}
              value={last_name}
              isInvalid={!!last_name_error}
            />
            {!!last_name_error && <p className="error-message">{last_name_error}</p>}
          </Form.Group>
        </Row>
        <Form.Group className="mb-3">
          <input type="checkbox" className="form-check-input me-2" id="accept_terms"
            checked={is_checked}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setChecked(e.target.checked)} />
          <Form.Label className="fs-5" htmlFor="accept_terms">I accept the&nbsp;
            <a target="_blank" href={SupportRoutes.terms_of_use} rel="noreferrer">PreVeil Terms of Service</a>
            &nbsp;and&nbsp;
            <a target="_blank" href={SupportRoutes.privacy_policy} rel="noreferrer">Privacy Policy</a></Form.Label>
        </Form.Group>
        <div className="btn-panel">
          <Button type="submit" disabled={!is_form_valid}>Create Account</Button>
          <Button variant="outline-secondary" type="button"
            onClick={() => handleAction({ actionType: CreateAccountUIActionTypes.ResetForms })}>Cancel</Button>
        </div>
      </Form>
    </Card.Body>
  </>;
}
export default React.memo(UserInformationFormComponent);
